import {
  useState,
  useEffect,
  FC,
  SetStateAction,
  Dispatch,
  useRef,
} from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import clsx from 'clsx';

import Print from '../../../../Icons/Print';
import Plus from '../../../../Icons/Plus';
import Minus from '../../../../Icons/Minus';
import { CrossIcon } from '../../../../Icons/Cross';

import 'react-pdf/dist/Page/TextLayer.css';
import { inElementInViewport } from '../../getCurrentPage';

import { getPartStatus } from '../../../../../api/part/getPart';

import styles from '../../SchemePage.module.scss';
import { IPart } from '../../SchemePage';
import { IGetProductResponse } from '../../../../../types/products';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface IReactPdfDocumentProps {
  setActiveDetail: Dispatch<SetStateAction<any>>;
  setDetails: Dispatch<SetStateAction<IPart[]>>;
  setFilteredDetails: Dispatch<SetStateAction<IPart[]>>;
  product: IGetProductResponse | null;
  details: IPart[];
  documentLink: string;
}

const ReactPdfDocument: FC<IReactPdfDocumentProps> = ({
  setActiveDetail,
  setDetails,
  setFilteredDetails,
  product,
  details,
  documentLink,
}) => {
  const [numPages, setNumPages] = useState<number>();
  const [scale, setScale] = useState<number>(0.125);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [hiddenHint, setHiddenHint] = useState(false);
  const [width, setWidth] = useState(840);
  const [fixedWidth, setFixedWidth] = useState(840);
  const holderRef = useRef<null | HTMLDivElement>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);

    setTimeout(() => {
      if (product) {
        const detailInDocument = document.querySelectorAll(`.textLayer span`);
        detailInDocument.forEach((el) => {
          const textContent = el.innerHTML;

          if (textContent.length < 6) return;

          getPartStatus(textContent.toUpperCase()).then((part) => {
            if (part.status !== 200) return;
            if (
              details.findIndex((item) => item.partCode === textContent) === -1
            ) {
              setDetails((state) => [
                ...state,
                { ...part.data, partCode: part.data.code },
              ]);
              setFilteredDetails((state) => [
                ...state,
                { ...part.data, partCode: part.data.code },
              ]);
            }
          });
        });
        document
          .querySelector('.react-pdf__Document')
          ?.addEventListener('scroll', () => {
            const pages = document.querySelectorAll(`.react-pdf__Page`);
            const currentPage = inElementInViewport([...pages]);
            setPageNumber(currentPage ? +currentPage : 1);
          });
      }
    }, 1000);
  };

  const onLoadError = () => {
    throw new Error('Ошибка при загрузке документа');
  };

  const onRenderTextLayerSuccess = (page: number) => {
    const detailInDocument = document.querySelectorAll(
      `[data-page-number="${page}"] .textLayer span`,
    );
    detailInDocument.forEach((el) => {
      const textContent = el.innerHTML;
      const index = details.findIndex((item) => item.partCode == textContent);
      if (index !== -1) {
        el.addEventListener('click', () => {
          setActiveDetail(details[index]);
        });
        (el as HTMLElement).style.cursor = 'pointer';
      }
    });
  };

  const updateScale = (type: 'plus' | 'minus') => {
    if (holderRef && holderRef.current) {
      const diff = (fixedWidth / 100) * 24;
      if (type === 'plus') setWidth((state) => Math.round(state + diff));
      if (type === 'minus') setWidth((state) => Math.round(state - diff));
    }
  };

  useEffect(() => {
    const resizeFunc = () => {
      setScale(0.1);
      const holderWidth = window.innerWidth - 32;
      if (window.innerWidth < 1024) {
        setWidth(holderWidth);
        setFixedWidth(holderWidth);
      } else {
        setWidth(840);
        setFixedWidth(840);
      }
    };
    window.addEventListener('resize', resizeFunc);
    resizeFunc();
    return () => window.removeEventListener('resize', resizeFunc);
  }, []);

  return (
    <>
      <div
        className={clsx(styles.hint_mob, {
          [styles.hidden]: hiddenHint,
        })}
      >
        <button onClick={() => setHiddenHint(true)}>
          <CrossIcon />
        </button>
        <p>Нажмите на код детали для информации</p>
      </div>
      <div className={styles.content_doc_controls}>
        <p>
          {pageNumber}/{numPages}
        </p>
        <a
          href={documentLink}
          target="_blank"
          rel="noreferrer"
          className={styles.print_mob}
        >
          <Print />
          Печать (PDF)
        </a>
        <div className={styles.scale}>
          <button
            onClick={() => {
              updateScale('minus');
              setScale((state) => +(state - 0.025).toFixed(3));
            }}
            disabled={scale <= 0.025}
          >
            <Minus />
          </button>
          <span>{scale * 1000}%</span>
          <button
            onClick={() => {
              updateScale('plus');
              setScale((state) => +(state + 0.025).toFixed(3));
            }}
          >
            <Plus />
          </button>
        </div>
      </div>
      <Document
        className={styles.document}
        file={documentLink}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onLoadError}
      >
        <div className={clsx(styles.hint, { [styles.hidden]: hiddenHint })}>
          <button onClick={() => setHiddenHint(true)}>
            <CrossIcon />
          </button>
          <p>Нажмите на код детали для информации</p>
        </div>
        <div
          ref={holderRef}
          className={styles.pages_holder}
          style={{ width: `${width}px` }}
        >
          {numPages &&
            Array.from(new Array(numPages), (_el, index) => (
              <Page
                className={styles.page}
                key={index + 1}
                renderAnnotationLayer={false}
                pageNumber={index + 1}
                onRenderTextLayerSuccess={() =>
                  onRenderTextLayerSuccess(index + 1)
                }
              />
            ))}
        </div>
      </Document>
    </>
  );
};

export default ReactPdfDocument;
